import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Strings from '../../../i18n';
import {
  ButtonStyled,
  ButtonStyledCancel,
  ButtonsContainer,
  InputStyled,
  TitleStyled,
  noBorderStyle
} from './addLocationToUserStyles';
import { colors } from '../../../constants/colors';
import { useGetUserSelectedLocationMutation } from '../../../services/device/deviceService';
import { LocationUserSelected } from '../../../types/location/location';
import { ItemLocationSelected } from '../../../components/itemLocationSelected/itemLocationSelected';
import { AddLocationToUserProps } from './addLocationToUserProps';
import { useMakeDefaultLocationByUserMutation } from '../../../services/locations/locationsService';

export const AddLocationToUser = ({
  reloadTable,
  setOpenRightSideBar,
  setUsersSelected,
  user
}: AddLocationToUserProps) => {
  const [filterText, setFilterText] = useState('');
  const [preLimit, setPreLimit] = useState(0);
  const [beforeLimit, setBeforeLimit] = useState(5);
  const [typingTimeout, setTypingTimeout] = useState<any | null>(null);
  const [locationSelected, setLocationSelected] = useState(0);
  const [locationsPreferred, setLocationsPreferred] = useState<
    LocationUserSelected[]
  >([]);
  const [getLocations, { isSuccess: isSuccessLocations, data: dataLocations }] =
    useGetUserSelectedLocationMutation();
  const [postUserToLocation, { isSuccess, isLoading }] =
    useMakeDefaultLocationByUserMutation();

  useEffect(() => {
    if (isSuccessLocations && dataLocations) {
      setLocationsPreferred(dataLocations.filter((loc) => loc.order !== 0));
    }
  }, [isSuccessLocations]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(Strings.locations.locationInUser);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    getLocations({
      userId: user ? user.userId?.toString() : '',
      searchText: filterText
    });
  }, []);

  const items = filterText.length ? dataLocations : locationsPreferred;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && filterText !== undefined) {
      getLocations({
        userId: user ? user.userId?.toString() : '',
        searchText: filterText.length ? filterText : ''
      });
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [filterText]);

  const handleChange = (value: string) => {
    setFilterText(value);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    if (value.length > 2) {
      setTypingTimeout(
        setTimeout(() => {
          getLocations({
            userId: user ? user.userId?.toString() : '',
            searchText: value
          });
        }, 1700)
      );
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  return (
    <>
      {isSuccessLocations && dataLocations && (
        <Box height="100%">
          <TitleStyled variant="h5">
            {Strings.locations.selectExistingLocation}
          </TitleStyled>
          <Box
            height="100%"
            bgcolor={colors.white}
            padding={2}
            marginTop={3}
            borderRadius={2}
          >
            <Grid direction="row" container>
              <Typography
                fontSize={13}
                color={colors.darkGray}
                marginTop={1}
                marginLeft={2}
              >
                {Strings.locations.searchByNameOwnerAddress}
              </Typography>
              <Grid
                item
                xs={12}
                bgcolor={colors.lightGray}
                padding={1}
                borderRadius={2}
              >
                <InputStyled
                  sx={noBorderStyle}
                  fullWidth
                  onChange={(e) => handleChange(e.target.value)}
                />

                {items &&
                  items.slice(preLimit, beforeLimit).map((loc) => (
                    <ItemLocationSelected
                      devices={loc.devicesCount}
                      selected={loc.id === locationSelected}
                      action={() => {
                        setLocationSelected(loc.id);
                      }}
                      locationName={loc.name}
                      ownerName={loc.owner}
                    />
                  ))}
              </Grid>
              <Grid
                item
                container
                justifyContent="flex-end"
                marginTop={1}
                marginBottom={1}
              >
                <ButtonStyledCancel
                  disabled={preLimit === 0 && beforeLimit === 5}
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setPreLimit((prev) => prev - 5);
                    setBeforeLimit((prev) => prev - 5);
                  }}
                >
                  {Strings.common.previous}
                </ButtonStyledCancel>
                <ButtonStyled
                  disabled={items && items?.length <= beforeLimit}
                  onClick={() => {
                    setPreLimit((prev) => prev + 5);
                    setBeforeLimit((prev) => prev + 5);
                  }}
                  variant="contained"
                  loading={isLoading}
                  size="small"
                >
                  {Strings.common.next}
                </ButtonStyled>
              </Grid>
            </Grid>
            <ButtonsContainer height="10%">
              <ButtonStyledCancel
                variant="contained"
                size="large"
                onClick={() => {
                  setUsersSelected([]);
                  setOpenRightSideBar(false);
                }}
              >
                {Strings.common.cancel}
              </ButtonStyledCancel>
              <ButtonStyled
                onClick={() =>
                  postUserToLocation({
                    locationId: locationSelected,
                    userId: user?.userId || 0
                  })
                }
                disabled={!locationSelected}
                variant="contained"
                loading={isLoading}
                size="large"
              >
                {Strings.common.select}
              </ButtonStyled>
            </ButtonsContainer>
          </Box>
        </Box>
      )}
      <div> </div>
    </>
  );
};
